import {
  AbsoluteCenter,
  Box,
  HStack,
  Image,
  ModalCloseButton,
  Text,
} from '@chakra-ui/react';
import { useEffect } from 'react';

import DeleteImg from '../../../../assets/images/delete-image.svg';
import {
  AnimatedLogo,
  CustomDrawerClosePrimaryButton,
  CustomModalActionPrimaryButton,
  CustomModalFooter,
  DangerButton,
  SecondaryButton,
  StatusBox,
} from '../../../../components/custom';
import { getTitleCase } from '../../../../helpers/utilityFunctions';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  deleteBeneficiaryAsync,
  resetBeneficiaryDelete,
} from '../../../../store/slices/beneficiaries/deleteBeneficiarySlice';
import {
  deleteBillBeneficiaryAsync,
  resetBillBeneficiaryDelete,
} from '../../../../store/slices/billPaymentBeneficiaries/deleteBillBeneficiarySlice';
import { IBeneficiary, IBillBeneficiary } from '../../../../types/beneficiary';

interface DeleteBeneficiaryConfirmationProps {
  type?: string;
  beneficiary?: IBeneficiary;
  billBeneficiary?: IBillBeneficiary;
}

export default function DeleteBeneficiaryConfirmation({
  type,
  beneficiary,
  billBeneficiary,
}: DeleteBeneficiaryConfirmationProps) {
  const dispatch = useAppDispatch();

  const { status, value } = useAppSelector((state) => state.beneficiaryDelete);
  const { status: billBeneficiaryStatus, value: billBeneficiaryValue } =
    useAppSelector((state) => state.deleteBillBeneficiary);

  const handleDelete = () => {
    if (type === 'bill') {
      dispatch(
        deleteBillBeneficiaryAsync({
          beneficiaryId: billBeneficiary?.id ?? '',
        })
      );
    } else
      dispatch(
        deleteBeneficiaryAsync({
          beneficiaryId: beneficiary?.id ?? '',
          accountNumber: beneficiary?.accountNumber ?? '',
        })
      );
  };

  useEffect(() => {
    if (type === 'bill') {
      dispatch(resetBillBeneficiaryDelete());
    } else dispatch(resetBeneficiaryDelete());
  }, [dispatch, type]);

  const alias =
    type === 'bill' ? billBeneficiary?.billAlias : beneficiary?.alias;

  const message =
    type === 'bill' ? billBeneficiaryValue?.message : value?.message;

  if (status === 'loading' || billBeneficiaryStatus === 'loading') {
    return (
      <Box
        minH="332px" // Totals to 500px
      >
        <AbsoluteCenter>
          <AnimatedLogo />
        </AbsoluteCenter>
      </Box>
    );
  }

  if (status === 'success' || billBeneficiaryStatus === 'success') {
    return (
      <>
        <StatusBox
          variant="success"
          title={`${alias} beneficiary deleted successfully.`}
          px="0"
        >
          {message ?? ''}
        </StatusBox>

        <CustomDrawerClosePrimaryButton>Close</CustomDrawerClosePrimaryButton>
      </>
    );
  }

  if (status === 'failed' || billBeneficiaryStatus === 'failed') {
    return (
      <>
        <StatusBox
          variant="error"
          title={`Failed to delete ${alias} beneficiary.`}
          px="0"
        >
          {message ?? `An error occurred while deleting ${alias} beneficiary`}
        </StatusBox>

        <CustomModalActionPrimaryButton
          onClick={() => {
            if (type === 'bill') {
              dispatch(resetBillBeneficiaryDelete());
            } else dispatch(resetBeneficiaryDelete());
          }}
        >
          Retry
        </CustomModalActionPrimaryButton>
      </>
    );
  }

  return (
    <>
      <Box pt={10} pb={4}>
        <Image
          src={DeleteImg}
          alt=""
          role="presentation"
          loading="lazy"
          boxSize={200}
          mx="auto"
          mb={4}
        />
        <Text fontSize="24px" lineHeight="133%" textAlign="center" mb={3}>
          Delete {getTitleCase(alias ?? '')}
        </Text>
        <Text color="gray.200" textAlign="center" letterSpacing="-0.12px">
          Are you sure you want to delete this beneficiary? This process can not
          be undone.
        </Text>
      </Box>
      <CustomModalFooter>
        <HStack spacing={4}>
          <DangerButton w="full" onClick={handleDelete}>
            Yes, Delete
          </DangerButton>
          <SecondaryButton
            w="full"
            as={ModalCloseButton}
            top="initial" // reset ModalCloseButton styles
            right="initial"
            flexShrink="initial"
          >
            Cancel, Keep
          </SecondaryButton>
        </HStack>
      </CustomModalFooter>
    </>
  );
}
